import {
  Avatar,
  Button,
  Card,
  CardActionArea,
  CardContent,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useCallback, useState } from 'react'
import { useCopyToClipboard } from 'usehooks-ts'
import { IconButton, Icons, InformationModal } from 'utoniq-ui'

import {
  ConsoleEventListFragment,
  EventStatus,
} from '@/clients/utoniq-core/schema'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { EventCategory, EventStatusTag } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { generateFullPath } from '@/lib/url'

export type ConsoleEventItemProps = {
  event: ConsoleEventListFragment
  artistId: string
  changingEventStatus: boolean
  deletingEvent: boolean
  onChangeEventStatus: (status: EventStatus) => Promise<void>
  onDeleteEvent: () => Promise<void>
}

export const ConsoleEventItem: Organism<ConsoleEventItemProps> = ({
  event,
  artistId,
  changingEventStatus,
  deletingEvent,
  onChangeEventStatus,
  onDeleteEvent,
}) => {
  const { t } = useTranslation('consoleEvents')
  const { generatePath, routerPages } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const collectionNumber = event.eventCollections.edges.filter(
    ({ node }) => node.collection.artistId === artistId
  ).length

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  return (
    <OrganismFrame>
      <Card elevation={0} sx={{ position: 'relative' }}>
        <CardContent sx={{ py: 2, px: 4, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={4}>
            <Avatar
              variant="square"
              src={event.thumbnail?.mediumUri ?? ''}
              sx={{ height: 70, width: 100 }}
            />
            <Stack flex={1} spacing={1} sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <EventCategory event={event} />
                <EventStatusTag status={event.publishStatus} />
              </Stack>
              {event.aboutDateRange && (
                <Typography variant="overline" color="text.secondary">
                  {t('eventDate', { date: event.aboutDateRange })}
                </Typography>
              )}
              <Typography variant="subtitle1" sx={styleSets.lineCamp(2)}>
                {event.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('collection', { number: collectionNumber })}
              </Typography>
            </Stack>
            <Stack>
              <IconButton.More
                size="xSmall"
                bgcolor="grey.100"
                color="grey.700"
                onClick={handleClick}
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  transform: 'rotate(90deg)',
                }}
              />
            </Stack>
          </Stack>
        </CardContent>
        <CardActionArea
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1,
          }}
          href={generatePath(routerPages.ConsoleShokufesEventDetail, {
            eventId: event.id,
          })}
        />
      </Card>
      <EventSettingCell
        eventId={event.id}
        anchorEl={anchorEl}
        status={event.status}
        changingEventStatus={changingEventStatus}
        deletingEvent={deletingEvent}
        onChangeEventStatus={onChangeEventStatus}
        onDeleteEvent={onDeleteEvent}
        onClose={() => setAnchorEl(null)}
      />
    </OrganismFrame>
  )
}

type EventSettingCellProps = {
  eventId: string
  status?: EventStatus
  anchorEl: null | HTMLElement
  onClose: () => void
  changingEventStatus: boolean
  deletingEvent: boolean
  onChangeEventStatus: (status: EventStatus) => Promise<void>
  onDeleteEvent: () => Promise<void>
}
export const EventSettingCell: Cell<EventSettingCellProps> = ({
  eventId,
  status = EventStatus.Unpublished,
  anchorEl,
  onClose,
  changingEventStatus,
  deletingEvent,
  onChangeEventStatus,
  onDeleteEvent,
}) => {
  const { t } = useTranslation('consoleEvents')
  const { router, generatePath, routerPages } = useRouter()
  const { showMessage, showError } = useMessage()
  const [_, copy] = useCopyToClipboard()
  const { onClick } = useBrowserLink(
    generatePath(routerPages.ShokufesEventDetail, { eventId })
  )
  const [openChangeStatusModal, setOpenChangeStatusModal] =
    useState<boolean>(false)
  const [openDeleteEventModal, setOpenDeleteEventModal] =
    useState<boolean>(false)

  const nextEventStatus =
    status === EventStatus.Published
      ? EventStatus.Unpublished
      : EventStatus.Published

  const options = [
    {
      label: t('copyLink', 'リンクをコピー'),
      async onClick() {
        await copy(
          generateFullPath(routerPages.ShokufesEventDetail, { eventId })
        )
        showMessage(t('copyURLSuccess'), 'success')
        onClose()
      },
    },
    {
      label: t('exploreApps', 'アプリを見る'),
      icon: (
        <ListItemIcon sx={{ '&.MuiListItemIcon-root': { minWidth: 24 } }}>
          <Icons.Window fontSize="xSmall" />
        </ListItemIcon>
      ),
      onClick() {
        onClick()
        onClose()
      },
    },
    {
      label: t('edit', '編集'),
      onClick() {
        router.push(
          generatePath(routerPages.ConsoleShokufesEventEdit, { eventId })
        )
        onClose()
      },
    },
    {
      label: t('delete', '削除'),
      onClick() {
        setOpenDeleteEventModal(true)
        onClose()
      },
    },
  ]

  const handleChangeStatus = () => {
    onChangeEventStatus(nextEventStatus)
      .then(() => {
        showMessage(
          status === EventStatus.Published
            ? t('unpublishedEventSuccess')
            : t('publishedEventSuccess')
        )
      })
      .catch(showError)
      .finally(() => setOpenChangeStatusModal(false))
  }

  const handleRemoveEvent = () => {
    onDeleteEvent()
      .then(() => {
        showMessage(t('deleteEventSuccess'))
      })
      .catch(showError)
      .finally(() => setOpenDeleteEventModal(false))
  }

  return (
    <CellFrame>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
        {options.map(({ icon, label, ...option }, index) => (
          <MenuItem {...option} sx={{ width: 200 }} key={index}>
            {icon}
            {label}
          </MenuItem>
        ))}
      </Menu>
      <InformationModal
        title={t(`${nextEventStatus}ModalTitle`)}
        content={t(`${nextEventStatus}ModalDesc`)}
        open={openChangeStatusModal}
        onClose={() => setOpenChangeStatusModal(false)}
        cancelText={t('close')}
        buttons={
          <Button
            color="primary"
            size="large"
            loading={changingEventStatus}
            disabled={changingEventStatus}
            onClick={handleChangeStatus}
            sx={{ minWidth: 212 }}
          >
            {t(`${nextEventStatus}ModalBtn`)}
          </Button>
        }
      />
      <InformationModal
        title={t('deleteEventModalTitle')}
        content={t('deleteEventModalDesc')}
        open={openDeleteEventModal}
        onClose={() => setOpenDeleteEventModal(false)}
        cancelText={t('close')}
        buttons={
          <Button
            color="primary"
            size="large"
            loading={deletingEvent}
            disabled={deletingEvent}
            onClick={handleRemoveEvent}
            sx={{ minWidth: 212 }}
          >
            {t('deleteEventBtn')}
          </Button>
        }
      />
    </CellFrame>
  )
}
